import {
  setupChatStartSchema,
  type SetupEntry,
  setupEntrySchema,
} from '@onestep/host-frame-protocol/org-setup';
import { createParser } from 'nuqs';
import { z } from 'zod';

const setupChatTargetSchema = z.object({
  orgRef: z.string().min(1),
  workspace: z.string().min(1),
  entry: setupEntrySchema,
  categoryName: z.string().optional(),
  cohortName: z.string().optional(),
  columnName: z.string().optional(),
  targetName: z.string().optional(),
  sessionId: z.string().min(1).optional(),
  start: setupChatStartSchema.optional().catch(undefined),
  chatCategory: z.string().min(1).optional(),
});
export type SetupChatTarget = z.infer<typeof setupChatTargetSchema>;
export const MAIN_CHAT_CATEGORY = 'main';
export const setupChatCategoryOf = ({
  entry,
  start,
  cohortName,
  categoryName,
}: Pick<SetupChatTarget, 'entry' | 'start' | 'cohortName' | 'categoryName'>): string | null => {
  switch (entry.kind) {
    case 'main':
      if (start === 'create-view') return 'create-view';
      return cohortName ? `edit-cohort:${cohortName}` : MAIN_CHAT_CATEGORY;
    case 'cohorts':
      return `cohorts:${categoryName ?? ''}`;
    case 'category':
      return `category:${entry.category}`;
    case 'columns':
      return entry.column ? `edit-column:${entry.column}` : 'columns';
    case 'draft':
      return `draft:${entry.working_copy.toUpperCase()}`;
    default:
      return null;
  }
};
export type SetupChatEntry = Exclude<SetupEntry, { kind: 'recipe' }>;
export type SetupChatOpenOptions = { switchWorkspace?: () => void };
export type OpenSetupChatInPlace = (
  target: SetupChatTarget,
  options?: SetupChatOpenOptions,
) => void;
export const setupChatPanelParser = createParser<SetupChatTarget>({
  parse: (value) => {
    try {
      const parsed = setupChatTargetSchema.safeParse(JSON.parse(value));
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  },
  serialize: JSON.stringify,
  eq: (a, b) => JSON.stringify(a) === JSON.stringify(b),
});
export const setupChatResumeTargetOf = ({
  orgRef,
  workspace,
  sessionId,
}: {
  orgRef: string;
  workspace: string;
  sessionId: string;
}): SetupChatTarget => ({ orgRef, workspace, entry: { kind: 'main' }, sessionId });
export const setupChatUrlOf = (target: SetupChatTarget) =>
  `/patients?${new URLSearchParams({ setup_chat: setupChatPanelParser.serialize(target) })}`;
export const setupChatEmbedUrlOf = ({ orgRef, workspace, sessionId }: SetupChatTarget) =>
  `/s/studio/embed/org/${encodeURIComponent(orgRef)}/workspace/${encodeURIComponent(workspace)}/setup${sessionId ? `/${encodeURIComponent(sessionId)}` : ''}`;
